import { TABLE_CELL_TYPE } from 'types/enums';
import { convertValueByType } from '../agendaRecord/agendaRecord.ts';
import type { DataSchema } from '../../types';
import { formatDateForBE, toUTCDate } from '../dateAndTime.ts';
import type { HeliosColumnsCellDataForBe } from '../../types/helios/agendaListing.ts';
import type { HeliosRowData } from '../hooks/userAgendaTable/useHeliosAgendaTableActions.ts';
import type { SaveRowData } from '../../types/helios/record.ts';
import type { FieldValues } from 'react-hook-form';
import type { DynamicRelationRecord, UpdateHeliosTableRecordsReq } from '../../types/dataHelios.ts';
import { isArray } from 'lodash';
import { isRawValueObject } from './mappers.ts';
import type { HeliosFileField } from './fileFields.ts';
import type { TableDirtyFields, TableFormData } from '../../components/common/Table/Table.tsx';
import { isHeliosNumericDataType, parseHeliosNumericRawValue } from './numberFormatting';
import type { RecordSection } from 'types/helios';

export const resolveStaticFileRawValue = (value: unknown): string | undefined => {
  if (!isArray(value)) return undefined;

  const rawItems = (value as HeliosFileField[]).filter((v) => v?.rawValue === true);
  if (rawItems.length === 0) return undefined;

  const removedIds = (value as HeliosFileField[]).filter((v) => v?.status === 'remove').map((v) => Number(v?.id));

  const activeItem = rawItems.find((v) => !removedIds.includes(Number(v?.id)));
  return activeItem?.id ? String(activeItem.id) : undefined;
};

const extractBrowseRawValue = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) {
    return typeof value === 'string' && value !== '' ? Number(value) : null;
  }

  const browseObj = value as { id?: unknown; name?: string; value?: unknown };

  if ('value' in browseObj && browseObj.value !== undefined && browseObj.value !== null) {
    if (typeof browseObj.value === 'object' && browseObj.value !== null && 'id' in browseObj.value) {
      return Number((browseObj.value as { id: unknown }).id);
    }
    return browseObj.value;
  }

  if ('id' in browseObj) {
    return Number(browseObj.id);
  }

  return null;
};

const processDynamicRelationValue = (value: unknown, templateItemId: number | undefined) => {
  if (!Array.isArray(value) || !templateItemId) {
    return {
      itemId: templateItemId ?? 0,
      addedDynamicRelationRecords: [] as DynamicRelationRecord[],
      removedDynamicRelationRecords: [] as DynamicRelationRecord[],
    };
  }

  const dynamicArray = value as Array<{ id: string; status: string }>;
  const added = dynamicArray.filter((item) => item.status === 'add').map((item) => ({ recordNumber: Number(item.id) }));
  const removed = dynamicArray.filter((item) => item.status === 'remove').map((item) => ({ recordNumber: Number(item.id) }));

  return {
    itemId: templateItemId,
    addedDynamicRelationRecords: added,
    removedDynamicRelationRecords: removed,
  };
};

interface ProcessRowDataProps {
  row: Record<string, { value: string | number | boolean | object; type: TABLE_CELL_TYPE }>;
  dataSchema: DataSchema;
}

export const processRowData = ({ row, dataSchema }: ProcessRowDataProps) =>
  Object.entries(row)
    .filter(([key]) => key !== 'id' && key !== 'identifier')
    .map(([key, value]) => {
      const schemaEntry = dataSchema[key];
      if (!schemaEntry) return null;

      if (schemaEntry.type === TABLE_CELL_TYPE.HELIOS_FILE) {
        if (!schemaEntry.settings?.externalFile?.dynamicRelationId) {
          if (typeof value.value === 'object' && value.value !== null) {
            const dynamicValue = value.value as { add?: string[]; remove?: string[] };
            if ('add' in dynamicValue || 'remove' in dynamicValue) {
              return {
                itemId: schemaEntry.templateItemId,
                rawValue: dynamicValue.add?.[0],
              };
            }
          }
          return {
            itemId: schemaEntry.templateItemId,
            rawValue: null,
          };
        }
        if (typeof value.value === 'object' && value.value !== null) {
          const dynamicValue = value.value as { add?: string[]; remove?: string[] };
          if ('add' in dynamicValue || 'remove' in dynamicValue) {
            return {
              itemId: schemaEntry.templateItemId,
              addedDynamicRelationRecords: (dynamicValue.add || []).map((id) => ({ recordNumber: Number(id) })),
              removedDynamicRelationRecords: (dynamicValue.remove || []).map((id) => ({ recordNumber: Number(id) })),
            };
          }
        }
        return {
          itemId: schemaEntry.templateItemId,
          addedDynamicRelationRecords: [] as DynamicRelationRecord[],
          removedDynamicRelationRecords: [] as DynamicRelationRecord[],
        };
      }

      if (schemaEntry.type === TABLE_CELL_TYPE.HELIOS_BROWSE) {
        const hasDynamicRelation = Boolean(schemaEntry.settings?.browse?.dynamicRelationId);

        if (hasDynamicRelation) {
          return processDynamicRelationValue(value.value, schemaEntry.templateItemId);
        }

        return {
          itemId: schemaEntry.templateItemId,
          rawValue: extractBrowseRawValue(value.value),
        };
      }

      if (value.value === '') {
        return {
          itemId: schemaEntry.templateItemId,
          rawValue: null,
        };
      }

      let convertedValue: unknown = value.value;
      switch (schemaEntry.dataType) {
        case 'Int32':
        case 'Int64':
        case 'Decimal':
          convertedValue = parseHeliosNumericRawValue(value.value, schemaEntry.dataType);
          break;
        case 'String':
          convertedValue = String(value.value);
          break;
        case 'Boolean':
          convertedValue = Boolean(value.value === 'true');
          break;
      }

      return {
        itemId: schemaEntry.templateItemId,
        rawValue: convertedValue,
      };
    })
    .filter(Boolean);

export const prepareRowData = (row: Record<string, RowFieldData>, data: SaveRowData): HeliosRowData => {
  const { templateId, dataSchema } = data;

  const columns = processRowData({ row, dataSchema }) as HeliosColumnsCellDataForBe[];
  return {
    templateId,
    columns,
  };
};

export const isNewRow = (rowId: string): boolean => rowId.startsWith('new');

export type RowData<P extends DataSchema> = {
  [key in keyof P]: RowFieldData;
};

export type RowFieldData = {
  value: string | number | boolean | object;
  type: TABLE_CELL_TYPE;
};

export const transformRowFieldData = <P extends DataSchema>(key: string, value: unknown, dataSchema: P): RowFieldData => {
  const type = dataSchema[key as keyof P].type;

  if (value instanceof Date) {
    return { value: toUTCDate(value), type };
  }
  if (typeof value === 'object' && value !== null) {
    return { value, type };
  }
  return { value: convertValueByType(value, type), type };
};

export type FormDataRows = Record<string, Record<string, RowFieldData>>;

export const processDirtyFieldsToRows = (dirtyFields: TableDirtyFields, formData: TableFormData, dataSchema: DataSchema): FormDataRows => {
  const rows: FormDataRows = {};

  Object.entries(dirtyFields).forEach(([rowId]) => {
    const row: Record<string, RowFieldData> = {};
    const cells = formData[rowId];

    Object.entries(cells as Record<string, unknown>).forEach(([cellId, value]) => {
      const schema = dataSchema[cellId];
      if (schema.isEditable) {
        row[cellId] = transformRowFieldData(cellId, value, dataSchema);
      }
    });

    rows[rowId] = row;
  });

  return rows;
};

export const normalizePrimitive = (value: unknown): string | null => (typeof value === 'object' ? null : (value as string));

export const serializeValue = (value: unknown) => {
  if (isArray(value)) {
    if (value.every((v) => 'fileId' in (v || {}))) {
      return value.map((v) => v?.fileId).join(', ');
    }
  }
  if (isRawValueObject(value as { id: string; name: string } | string)) {
    const objValue = value as { id: string; name: string; value?: unknown };
    if ('value' in objValue && objValue.value !== undefined && objValue.value !== null) {
      if (typeof objValue.value === 'object' && objValue.value !== null && 'id' in objValue.value) {
        return (objValue.value as { id: unknown }).id;
      }
      return objValue.value;
    }
    return objValue.id;
  }
  if (value instanceof Date) {
    return formatDateForBE(value);
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return formatDateForBE(value) ?? normalizePrimitive(value);
  }
  return normalizePrimitive(value);
};

export const buildHeliosFormItemDataTypes = (sections: RecordSection[] | undefined): Record<string, string> => {
  const dataTypeMap: Record<string, string> = {};

  sections?.forEach((section) => {
    section.formItems?.forEach(({ formItem }) => {
      const dataType = formItem?.definition?.dataRecordColumn?.dataType;

      if (formItem?.id && dataType) {
        dataTypeMap[formItem.id] = dataType;
      }
    });
  });
  return dataTypeMap;
};

export const buildColumns = (items: FieldValues, dataTypes?: Record<string, string>): UpdateHeliosTableRecordsReq['columns'] =>
  Object.entries(items)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]): UpdateHeliosTableRecordsReq['columns'][number] => {
      if (isArray(value) && value.some((v: { rawValue?: boolean } | undefined) => v?.rawValue === true)) {
        return { itemId: Number(key), rawValue: resolveStaticFileRawValue(value) };
      }

      if (isArray(value) && value.some((v) => v?.status === 'add' || v?.status === 'remove')) {
        let addedRecords = value
          .filter((v) => v?.status === 'add')
          .map((v) => ({ recordNumber: Number(v?.id) }))
          .filter((r) => r.recordNumber);

        let removedRecords = value
          .filter((v) => v?.status === 'remove')
          .map((v) => ({ recordNumber: Number(v?.id) }))
          .filter((r) => r.recordNumber);

        const duplicateIds = addedRecords.map((r) => r.recordNumber).filter((id) => removedRecords.map((r) => r.recordNumber).includes(id));
        if (duplicateIds.length) {
          addedRecords = addedRecords.filter((r) => !duplicateIds.includes(r.recordNumber));
          removedRecords = removedRecords.filter((r) => !duplicateIds.includes(r.recordNumber));
        }

        return {
          itemId: Number(key),
          addedDynamicRelationRecords: addedRecords.length ? addedRecords : [],
          removedDynamicRelationRecords: removedRecords.length ? removedRecords : [],
        };
      }

      if (isRawValueObject(value)) {
        return {
          itemId: Number(key),
          rawValue: serializeValue(value) as string | undefined,
        };
      }

      const dataType = dataTypes?.[key];
      if (dataType && isHeliosNumericDataType(dataType)) {
        const parsed = parseHeliosNumericRawValue(value, dataType);
        return {
          itemId: Number(key),
          rawValue: parsed != null ? String(parsed) : undefined,
        };
      }

      return {
        itemId: Number(key),
        rawValue: serializeValue(value) as string | undefined,
      };
    });
